import React, { useCallback } from 'react';
import Joyride, { ACTIONS, EVENTS, STATUS } from 'react-joyride';
import { useTour } from '../context/TourContext';

// ── Tour steps ────────────────────────────────────────────────────────────────

const STEPS = [
  {
    target: 'body',
    placement: 'center',
    disableBeacon: true,
    title: 'Welcome to SecurePoint Central',
    content:
      'This quick tour will walk you through the key areas of the platform so you can get started confidently. You can skip at any time and restart the tour from the Help button in the top bar.',
  },
  {
    target: '[data-tour="sidebar-logo"]',
    placement: 'right',
    disableBeacon: true,
    title: 'SecurePoint Central',
    content:
      'This is your navigation hub. The sidebar groups all platform sections by category. You can collapse it to icon-only mode using the menu button at the top.',
  },
  {
    target: '[data-tour="nav-overview"]',
    placement: 'right',
    disableBeacon: true,
    title: 'Overview',
    content:
      'The Dashboard gives you a real-time snapshot of your entire environment — threats, device health, compliance, and risk score. Analytics provides deeper historical trends.',
  },
  {
    target: '[data-tour="nav-endpoints"]',
    placement: 'right',
    disableBeacon: true,
    title: 'Endpoints',
    content:
      'Manage all enrolled devices, push software deployments, remotely control endpoints, and track pending system updates from this section.',
  },
  {
    target: '[data-tour="nav-threat-defense"]',
    placement: 'right',
    disableBeacon: true,
    title: 'Threat Defense',
    content:
      'Your security arsenal — EDR for endpoint detection and response, Threat Intelligence feeds, DLP to prevent data leakage, Forensics for incident investigation, and more.',
  },
  {
    target: '[data-tour="nav-compliance"]',
    placement: 'right',
    disableBeacon: true,
    title: 'Compliance & Policy',
    content:
      'Define and enforce security policies, run compliance audits against your rules, review the overall security posture, and monitor endpoint health scores.',
  },
  {
    target: '[data-tour="nav-operations"]',
    placement: 'right',
    disableBeacon: true,
    title: 'Operations',
    content:
      'Generate reports for stakeholders, manage platform configurations, browse audit logs, and schedule or restore backups from this section.',
  },
  {
    target: '[data-tour="nav-administration"]',
    placement: 'right',
    disableBeacon: true,
    title: 'Administration',
    content:
      'Manage users and roles, configure tenant settings, create API keys for integrations, set up webhooks, and connect third-party tools. Admin-only access.',
  },
  {
    target: '[data-tour="stat-cards"]',
    placement: 'bottom',
    disableBeacon: true,
    title: 'Key Metrics at a Glance',
    content:
      'These five cards give you the pulse of your environment — total enrolled devices, how many are online right now, active security policies, compliance rate, and any critical active threats. Click any card to drill into its detail page.',
  },
  {
    target: '[data-tour="stat-total-devices"]',
    placement: 'bottom',
    disableBeacon: true,
    title: 'Total Devices',
    content:
      'The total count of all enrolled endpoints across your organization. Click to open the full device inventory where you can filter, sort, and take action on individual devices.',
  },
  {
    target: '[data-tour="stat-online"]',
    placement: 'bottom',
    disableBeacon: true,
    title: 'Online Now',
    content:
      'Devices that have checked in within the last 5 minutes. A large gap between Total and Online may indicate connectivity issues or devices that need attention.',
  },
  {
    target: '[data-tour="stat-policies"]',
    placement: 'bottom',
    disableBeacon: true,
    title: 'Active Policies',
    content:
      'The number of security and compliance policies currently enforced across your fleet. Click to manage policies, check assignments, and review rule sets.',
  },
  {
    target: '[data-tour="stat-compliance"]',
    placement: 'bottom',
    disableBeacon: true,
    title: 'Compliance Rate',
    content:
      'The percentage of enrolled devices meeting all active policy requirements. Green is above 90%, orange above 70%, red below 70%. Click to see the full compliance report.',
  },
  {
    target: '[data-tour="stat-threats"]',
    placement: 'bottom',
    disableBeacon: true,
    title: 'Critical Threats',
    content:
      'Active critical-severity security events requiring immediate attention. A non-zero count means action is needed. Click to jump directly into the EDR alert queue.',
  },
  {
    target: '[data-tour="threat-timeline"]',
    placement: 'top',
    disableBeacon: true,
    title: 'Threat Timeline',
    content:
      'An area chart showing the volume of Critical, High, and Medium severity events over the past 7 days. Spikes indicate periods of increased threat activity — hover over any point to see the breakdown for that day.',
  },
  {
    target: '[data-tour="device-health"]',
    placement: 'top',
    disableBeacon: true,
    title: 'Device Health',
    content:
      'A donut chart breaking down your fleet by connection status — Online (green), Warning (orange), Offline (red), and Unknown (grey). Hover a segment to see the count.',
  },
  {
    target: '[data-tour="security-events"]',
    placement: 'top',
    disableBeacon: true,
    title: 'Security Events',
    content:
      'A severity breakdown of all security events detected — Critical, High, Medium, and Low. Click "View All" to open the full security event log with filtering and export options.',
  },
  {
    target: '[data-tour="platform-distribution"]',
    placement: 'top',
    disableBeacon: true,
    title: 'Platform Distribution',
    content:
      'Shows how your enrolled devices are split across operating systems (Windows, macOS, Linux). Useful for understanding coverage gaps or planning platform-specific policy rollouts.',
  },
  {
    target: '[data-tour="risk-score"]',
    placement: 'top',
    disableBeacon: true,
    title: 'Risk Score',
    content:
      'An environment risk gauge derived from your compliance rate and active threat count. Lower is better — green means low risk, orange medium, red high. Reducing critical threats and improving compliance directly lowers this score.',
  },
  {
    target: '[data-tour="quick-actions"]',
    placement: 'top',
    disableBeacon: true,
    title: 'Quick Actions',
    content:
      'One-click shortcuts to the most common workflows — jump to reports, manage devices, review security alerts, push a deployment, or run a policy compliance check. These shortcuts are also accessible from the sidebar.',
  },
  {
    target: '[data-tour="notification-center"]',
    placement: 'bottom',
    disableBeacon: true,
    title: 'Notification Center',
    content:
      'Real-time system alerts land here — new threats detected, policy violations, deployment results, and platform health warnings. Unread notifications are shown with a badge count.',
  },
  {
    target: '[data-tour="user-menu"]',
    placement: 'bottom-end',
    disableBeacon: true,
    title: 'Your Account',
    content:
      'Access your profile, change settings, enable two-factor authentication, manage your API keys, and sign out. Your assigned role (Admin / Operator / Viewer) controls which sections you can access.',
  },
  {
    target: '[data-tour="tour-button"]',
    placement: 'bottom',
    disableBeacon: true,
    title: 'Replay This Tour Anytime',
    content:
      'You can restart this tour at any point by clicking the Help icon here. All steps will replay from the beginning.',
  },
  {
    target: 'body',
    placement: 'center',
    disableBeacon: true,
    title: "You're All Set!",
    content:
      "You've completed the SecurePoint Central tour. Start by enrolling your first device or reviewing existing security policies. The sidebar is always there to navigate — good luck!",
  },
];

// ── Dark-theme Joyride styles (matches MUI dark palette) ─────────────────────

const JOYRIDE_STYLES = {
  options: {
    arrowColor: '#1a2035',
    backgroundColor: '#1a2035',
    overlayColor: 'rgba(10, 13, 20, 0.72)',
    primaryColor: '#00b4d8',
    spotlightShadow: '0 0 0 3px rgba(0, 180, 216, 0.25), 0 0 40px rgba(0, 180, 216, 0.15)',
    textColor: '#c8d0e0',
    width: 360,
    zIndex: 10000,
  },
  tooltip: {
    borderRadius: 12,
    border: '1px solid rgba(255,255,255,0.08)',
    padding: '0px',
    boxShadow: '0 24px 48px rgba(0,0,0,0.6)',
    overflow: 'hidden',
  },
  tooltipContainer: {
    padding: '20px 24px 16px',
    textAlign: 'left',
  },
  tooltipTitle: {
    color: '#e8eaf6',
    fontSize: '0.95rem',
    fontWeight: 700,
    marginBottom: 8,
    fontFamily: '"Inter", "Roboto", sans-serif',
  },
  tooltipContent: {
    fontSize: '0.81rem',
    lineHeight: 1.65,
    color: '#8892b0',
    fontFamily: '"Inter", "Roboto", sans-serif',
    marginTop: 0,
  },
  tooltipFooter: {
    padding: '12px 24px 16px',
    borderTop: '1px solid rgba(255,255,255,0.05)',
    marginTop: 12,
    gap: 8,
  },
  buttonNext: {
    backgroundColor: '#00b4d8',
    borderRadius: 6,
    fontSize: '0.8rem',
    fontWeight: 600,
    padding: '7px 18px',
    color: '#000',
    outline: 'none',
    cursor: 'pointer',
  },
  buttonBack: {
    color: '#8892b0',
    fontSize: '0.8rem',
    marginRight: 4,
    cursor: 'pointer',
  },
  buttonSkip: {
    color: '#546e7a',
    fontSize: '0.75rem',
    cursor: 'pointer',
  },
  buttonClose: {
    color: '#546e7a',
    top: 12,
    right: 14,
  },
  spotlight: {
    borderRadius: 8,
  },
};

// ── Component ─────────────────────────────────────────────────────────────────

function TourGuide() {
  const { run, setRun, stepIndex, setStepIndex, stopTour } = useTour();

  const handleCallback = useCallback(
    (data) => {
      const { action, index, status, type } = data;

      if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
        stopTour();
        return;
      }

      if (type === EVENTS.STEP_AFTER || type === EVENTS.TARGET_NOT_FOUND) {
        // Advance or go back
        setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
      }
    },
    [stopTour, setStepIndex],
  );

  return (
    <Joyride
      steps={STEPS}
      run={run}
      stepIndex={stepIndex}
      continuous
      scrollToFirstStep
      showProgress
      showSkipButton
      disableOverlayClose
      spotlightClicks={false}
      styles={JOYRIDE_STYLES}
      locale={{
        back:  'Back',
        close: 'Close',
        last:  'Finish',
        next:  'Next',
        open:  'Start tour',
        skip:  'Skip tour',
      }}
      callback={handleCallback}
    />
  );
}

export default TourGuide;
